<script lang="ts">
	import { NewsProfileCardGrid } from '../NewsProfile/NewsProfileCardGrid';
	import { NewsProfileFeedBar } from './NewsProfileFeedBar';
	import { LoadMoreInfinite } from '#lib/components/LoadMoreInfinite/index.js';
	import { FeatureFlags } from '#lib/constants/feature-flags.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { NewsCardGrid } from '#lib/features/news/components/NewsCardGrid/index.js';
	import { HENI_NEWS_TITLE } from '#lib/websites/news/constants/heni-tag.js';
	import { CircularProgress } from '$global/components/CircularProgress';
	import { Container } from '$global/components/Container';
	import { LinkButton } from '$global/components/LinkButton';
	import { Txt } from '$global/components/Txt';
	import { capitalizeFirstLetters } from '$global/utils/capitalizeFirstLetters';

	interface Props {
		articles:
			| Array<{
					id: string;
					title?: string | null;
					description?: string | null;
					image?: string;
					categories?: string[];
					geotag?: { name: string } | null;
					artists?: Array<
						| { HENI_News_Artists_id?: { name?: string } | null }
						| null
						| undefined
					>;
					url?: string | null;
					attribution?: string | null;
					date_created?: string | null;
					date_updated?: string | null;
					publishDate?: string | null;
			  }>
			| undefined;
		loading: boolean;
		loadingNextPage: boolean;
		showGlobalNav: boolean;
		phrase: string | null;
		geotag: string | null;
		artist: string | null;
		category?: string | null;
		isHeni?: boolean;
		// Whether a profile hero renders above this feed — it changes which grid
		// layout is safe to use (see the widget-column note below).
		hasProfileAbove?: boolean;
		onClick: () => void;
		onLoadMore: () => void;
		search: { set: (value: string) => void };
	}

	let {
		articles,
		loading,
		loadingNextPage,
		showGlobalNav,
		phrase,
		geotag,
		artist,
		category = null,
		isHeni = false,
		hasProfileAbove = false,
		onClick,
		onLoadMore,
		search,
	}: Props = $props();

	let term = $derived(phrase || geotag || artist);

	// `term` is always null inside the no-results branch below, so the category
	// (which filters the feed without being a `term`) is what names that page.
	// The HENI feed is not a category, so it names itself.
	let emptyStateLabel = $derived(
		(() => {
			if (isHeni) {
				return HENI_NEWS_TITLE;
			}

			return category ? capitalizeFirstLetters(category) : null;
		})()
	);
</script>

{#if !loading && !articles?.length && !term}
	<Container
		dataCy="news-home-no-results"
		class="flex min-h-[calc(100vh-96px)] flex-col items-center justify-center sm:min-h-[calc(100vh-148px)] md:min-h-[calc(100vh-157px)] lg:min-h-[calc(100vh-153px)]"
	>
		<Txt variant="h5" component="p" class="mb-4 text-center">
			{#if emptyStateLabel}
				No results were found for
				<span class="font-500">'{emptyStateLabel}'</span>
			{:else}
				No results were found
			{/if}
		</Txt>
		<LinkButton
			dataCy="news-home-back-home-page"
			variant="secondary"
			size="md"
			onclick={() => search.set('')}
			href={`${GlobalRoutes.NewsHome}${showGlobalNav ? '' : '?global_nav=0'}`}
		>
			Back to home page
		</LinkButton>
	</Container>
{:else}
	{#if term}
		<NewsProfileFeedBar {phrase} {geotag} {artist} />

		{#if !loading && !articles?.length}
			<Txt variant="h5" component="p" class="mt-12 mb-6 text-center">
				No articles were found for the selected category.
			</Txt>
		{/if}

		<NewsProfileCardGrid {articles} {loading} {onClick} />
	{/if}

	{#if !term}
		{#if isHeni}
			<Container dataCy="news-heni-title" class="pt-6 sm:pt-8 lg:pt-12">
				<Txt variant="h3" component="h1">{HENI_NEWS_TITLE}</Txt>
			</Container>
		{/if}

		{#if hasProfileAbove}
			<!-- Mirrors the artist/geotag profile layout (NewsProfileCardGrid): the
			widget column is position:fixed and its scroll math assumes the grid
			starts below the header, so it rides up over any hero above it. Those
			pages avoid that by only using the fixed column below `sm` and switching
			to the classic grid from `sm` up — the HENI feed does the same whenever a
			profile renders above it. -->
			<NewsCardGrid
				{articles}
				classes={{ container: 'pt-4 sm:hidden' }}
				{loading}
				{onClick}
				widgetMode="column"
			/>

			<NewsCardGrid
				{articles}
				classes={{ container: 'hidden pt-4 sm:block sm:pt-8 lg:pt-12' }}
				{loading}
				{onClick}
				widgetMode="classic"
			/>
		{:else}
			<NewsCardGrid
				{articles}
				classes={{
					// The heading above already carries the top spacing when it renders.
					container: isHeni ? 'pt-4' : 'sm:pt-8 lg:pt-12',
				}}
				{loading}
				{onClick}
				widgetMode={'column'}
			/>
		{/if}
	{/if}

	<div class="mb-9">
		<LoadMoreInfinite
			dataCy="news-home-page"
			hasMore
			{onLoadMore}
			threshold={3000}
			initialLoadMoreButton={FeatureFlags.loadButtonBeforeInfiniteScroll}
			classes={{ button: 'mt-6' }}
			trackPrefix="NEWS"
			loadedCount={articles?.length || 0}
		/>
	</div>
{/if}

{#if loadingNextPage}
	<div class="flex justify-center py-12">
		<CircularProgress dataCy="news-home-page" />
	</div>
{/if}
